import { React, useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { useLanguage } from '../../../../Context/LanguageContext';
import { useTheme } from '../../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../../Styles/theme';

import PartnerGymHoldingAndExtendHistoryInfoView from './PartnerGymHoldingAndExtendHistoryInfoView';
import PartnerGymRefundInfoView from './PartnerGymRefundInfo';

const PartnerGymMemberShipHistoryInfoView = (props) => {
  const {
    data,
    index,
    size,
    firstExpiredIndex = -1,
  } = props;

  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [fontColor, setFontColor] = useState(theme.text);
  const [border, setBorder] = useState(`1px solid ${theme.subBackground}`);
  const [hoverBorder, setHoverBorder] = useState(`1px solid ${theme.subBackground}`);
  const [backgroundColor, setBackgroundColor] = useState(theme.subBackground);

  useEffect(() => {
    const startDate = moment(data.startDate);
    const endDate = moment(data.endDate);

    if (data.isRefund) {
      setFontColor(theme.subText);
      setBorder(`1px solid ${theme.buttonFill}`);
      setHoverBorder(`1px solid ${theme.primary}`);
      setBackgroundColor(theme.buttonFill);
    }
    else if (data.isExpectedMemberShip) {
      setFontColor(theme.text);
      setBorder(`1px solid ${theme.subBackground}`);
      setHoverBorder(`1px solid ${theme.primary}`);
      setBackgroundColor(theme.subBackground);
    }
    else if (moment().isBefore(startDate)) {
      setFontColor(theme.subText);
      setBorder(`1px solid ${theme.subBackground}`);
      setHoverBorder(`1px solid ${theme.primary}`);
      setBackgroundColor(theme.subBackground);
    }
    else if (moment().isBefore(endDate)) {
      if (data.totalMax !== -1
        && data.totalMax <= data.courseTakeCount
        && (data.lastReserveDate !== undefined && moment().startOf('day').isAfter(data.lastReserveDate))) {
        setFontColor(theme.subText);
        setBorder(`1px solid ${theme.buttonFill}`);
        setHoverBorder(`1px solid ${theme.primary}`);
        setBackgroundColor(theme.buttonFill);
      }
      else {
        setFontColor(theme.text);
        setBorder(`1px solid ${theme.subBackground}`);
        setHoverBorder(`1px solid ${theme.primary}`);
        setBackgroundColor(theme.subBackground);
      }
    }
    else {
      setFontColor(theme.subText);
      setBorder(`1px solid ${theme.buttonFill}`);
      setHoverBorder(`1px solid ${theme.primary}`);
      setBackgroundColor(theme.buttonFill);
    }
  }, [data, isDark])

  // 구독 상태 결정 //
  function decideSubscriptionStatus() {
    if (data.subscribeStatus && !data.subscribeDeleted) return 'subscribing';
    if (moment().isBefore(moment(data.startDate))) return 'expected';
    if (data.isExpectedMemberShip) return 'waiting';
    return 'ended';
  }

  // 결제 금액 정리 //
  function decideTotalPriceView() {
    if (data.selectedPaymentOptions === undefined || data.selectedPaymentOptions.length === 0) {
      if (data.cardPrice === 0 && data.cashPrice === 0) return t('memberDetail.payFree');
      else if (data.cardPrice === 0) return data.cashPrice.toLocaleString('ko-KR') + ' - ' + t('memberDetail.payCash');
      else if (data.cashPrice === 0) return data.cardPrice.toLocaleString('ko-KR') + ' - ' + t('memberDetail.payCard');
      else return (parseInt(data.cashPrice) + parseInt(data.cardPrice)).toLocaleString('ko-KR') + ' - ' + t('memberDetail.payCombined');
    }
    else {
      const allSame = data.selectedPaymentOptions.every(item => item.name === data.selectedPaymentOptions[0].name);
      const totalPrice = data.selectedPaymentOptions.reduce((sum, item) => sum + item.price, 0);

      const paymentNameMap = { '카드': t('memberDetail.payCard'), '현금': t('memberDetail.payCash'), '포인트': t('memberDetail.payPoint') };
      if (allSame) return totalPrice.toLocaleString('ko-KR') + ' - ' + (paymentNameMap[data.selectedPaymentOptions[0].name] || data.selectedPaymentOptions[0].name);
      else return totalPrice.toLocaleString('ko-KR') + ' - ' + t('memberDetail.payCombined');
    }
  }

  // 남은일수에 맞춰 텍스트 정리 //
  function remainDaysView() {
    if (data.isRefund) return t('memberDetail.statusRefund');
    else if (data.isExpectedMemberShip) return t('memberDetail.statusPaymentPending');
    else if (data.totalMax === data.courseTakeCount
      && (data.lastReserveDate !== undefined && (moment().startOf('day').isAfter(data.lastReserveDate)))) return t('memberDetail.statusExpired');
    // 사용 시작 전(사용예정)은 금일이 아닌 회원권 기간으로 계산 //
    else if (moment().isBefore(moment(data.startDate))) {
      return (moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment(data.startDate).format('YYYY-MM-DD')), 'days') + 1) + t('memberDetail.daysSuffix');
    }
    else if (moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'days') > 0) {
      return moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'days') + t('memberDetail.daysSuffix');
    }
    else if (moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'days') === 0) {
      return 'D-Day'
    }
    return t('memberDetail.statusExpired');
  }

  // 수강 횟수 표시 //
  function decideUseTotalMax() {
    if (data.totalMax === -1) return data.courseTakeCount + '/∞';
    else return data.courseTakeCount + '/' + data.totalMax;
  }

  return (
    <TopLevelWrapper>
      { (firstExpiredIndex !== -1 && firstExpiredIndex === index) && <ExpiredDivisionLine/> }
      <MemberShipComponent
        $isDark={isDark}
        zIndex={20 + size - index}
        border={border}
        hoverBorder={hoverBorder}
        backgroundColor={backgroundColor}
        isPartial={data.isRefund || data.historyData.length > 0}>
        <Table>
          <Body>
            <Tr style={{ width: '270px' }}>
              <Td>
                <TextWrapper style={{ maxWidth: '270px', gap: '4px' }}>
                  {
                    (data.isSubscriptionMembership || data.isExpectedMemberShip || moment().isBefore(moment(data.startDate))) &&
                    <SubscriptionBox statusType={decideSubscriptionStatus()}>
                      <SubscriptionText>
                        {decideSubscriptionStatus() === 'subscribing' ? t('memberDetail.statusSubscribing')
                          : decideSubscriptionStatus() === 'expected' ? t('memberDetail.statusScheduled')
                          : decideSubscriptionStatus() === 'waiting' ? t('memberDetail.statusWaiting')
                          : t('memberDetail.statusSubscriptionEnded')}
                      </SubscriptionText>
                    </SubscriptionBox>
                  }
                  <Text color={fontColor}>{data.memberShipName}</Text>
                </TextWrapper>
              </Td>
            </Tr>
            <Tr style={{ width: '145px' }}>
              <Td>
                <TextWrapper style={{ maxWidth: '135px' }}>
                  <Text color={fontColor}>
                    {moment(data.startDate).format(t('partnerGym.history.dateFormat'))} - {moment(data.endDate).format(t('partnerGym.history.dateFormat'))}
                  </Text>
                </TextWrapper>
              </Td>
            </Tr>
            <Tr style={{ width: '190px' }}>
              <Td>
                <TextWrapper style={{ maxWidth: '180px' }}>
                  {(() => {
                    const result = decideTotalPriceView();
                    const dashIdx = result.indexOf(' - ');
                    if (dashIdx === -1) return <Text color={fontColor}>{result}</Text>;
                    return (
                      <PaymentTextWrapper>
                        <Text color={fontColor} style={{ flexShrink: 0 }}>{result.slice(0, dashIdx)}</Text>
                        <PaymentMethodText color={fontColor}>{result.slice(dashIdx)}</PaymentMethodText>
                      </PaymentTextWrapper>
                    );
                  })()}
                </TextWrapper>
              </Td>
            </Tr>
            <Tr style={{ width: '80px' }}>
              <Td>
                <TextWrapper style={{ maxWidth: '70px' }}>
                  <Text color={fontColor}>{data.notReceivedPrice.toLocaleString('ko-KR')}</Text>
                </TextWrapper>
              </Td>
            </Tr>
            <Tr style={{ width: '4px' }}/>
            <Tr style={{ width: '65px' }}>
              <Td>
                <TextWrapper style={{ maxWidth: '65px' }}>
                  <Text color={fontColor}>{remainDaysView()}</Text>
                </TextWrapper>
              </Td>
            </Tr>
            <Tr style={{ width: '55px' }}>
              <Td>
                <TextWrapper style={{ maxWidth: '55px' }}>
                  <Text color={fontColor}>{decideUseTotalMax()}</Text>
                </TextWrapper>
              </Td>
            </Tr>
          </Body>
        </Table>
      </MemberShipComponent>
      <SubBackgroundCover zIndex={20 + size - index - 1}/>
      {
        data.isRefund &&
        <ExpertComponentWrapper zIndex={data.historyData.length + 1}>
          <PartnerGymRefundInfoView data={data}/>
        </ExpertComponentWrapper>
      }
      {
        data.historyData.map((innerData, index) => (
          <ExpertComponentWrapper key={'PartnerGymHolding-' + index} zIndex={data.historyData.length - index}>
            <PartnerGymHoldingAndExtendHistoryInfoView
              data={innerData} />
          </ExpertComponentWrapper>
        ))
      }
    </TopLevelWrapper>
  )
};

export default PartnerGymMemberShipHistoryInfoView;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 970px;
`;

const MemberShipComponent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  width: 970px;
  height: 52px;

  z-index: ${props => props.zIndex};

  border: ${props => props.border};
  border-radius: ${props => props.isPartial ? '30px 10px 0px 30px' : '30px'};
  background-color: ${props => props.backgroundColor};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
      border: ${props => props.hoverBorder};
    }
  }
`;

const ExpiredDivisionLine = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin: 20px 0px;

  width: 935px;
  height: 1px;

  background-color: ${props => props.theme.line};
`;

const Table = styled.table`
  display: flex;
  flex-direction: row;
`;

const Body = styled.tbody`
  display: flex;
  flex-direction: row;

  align-items: center;

  margin-left: 15px;

  gap: 15px;
`;

const Tr = styled.tr`
  display: flex;
  flex-direction: row;
`;

const Td = styled.td`
  display: flex;
  flex-direction: row;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: flex-start;

  overflow: hidden;

  max-height: 20px;
`;

const Text = styled.span`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  padding: 0 0 0 0;
`;

const PaymentTextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: flex-start;
`;

const PaymentMethodText = styled.span`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const SubscriptionBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 0px 5px;
  margin-top: 2px;

  min-width: 30px;
  min-height: 18px;

  border-radius: 5px;
  background-color: ${props =>
    props.statusType === 'subscribing' ? props.theme.primary
    : props.statusType === 'ended' ? props.theme.primary
    : props.statusType === 'expected' ? props.theme.pointYellow
    : props.theme.subKey};
`;

const SubscriptionText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 12px;

  color: ${props => props.theme.subBackground};

  margin: -2px 0 0 0;
  padding: 0 0 0 0;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: default;
    }
  }
`;

const SubBackgroundCover = styled.div`
  position: relative;

  width: 1018px;
  height: 50px;

  margin-top: -50px;

  z-index: ${props => props.zIndex};

  border-radius: 30px;
  background-color: ${props => props.theme.background};
`;

const ExpertComponentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;

  z-index: ${props => props.zIndex};
`;
